import { useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddBoxOutlined from '@mui/icons-material/AddBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConstructionOutlined from '@mui/icons-material/ConstructionOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import FactoryOutlined from '@mui/icons-material/FactoryOutlined';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import { specMaterial, specText, type SkuUsage, type SpecField } from '@zilian/contracts';
import { Tag } from '@zilian/ui';
import { api } from './api';
import { FieldLabel, Note, ON_SURFACE, Section, SURFACE } from './FormParts';
import { MaterialRows } from './MaterialRows';
import { byWeight, partsFor, type RecipeLine } from './recipe';
import { RecipeTable } from './RecipeTable';
import { SpecPanel } from './SpecPanel';
import { decimalInput, plain, PRICE_DECIMALS } from './ui';

export interface CreatedSku {
  id: string;
  code: string;
  name: string;
  spec: string;
  unit: string;
  usage: SkuUsage;
  packSize: string | null;
  salesPrice: string | null;
}

export interface Category {
  id: string;
  name: string;
  /** What its items' codes start with: CAP-0001. */
  codePrefix: string;
  fields: SpecField[];
}

interface Props {
  defaultUsage: SkuUsage;
  allowedUsages?: SkuUsage[];
  title?: string;
  categories?: Category[];
  /** An existing item to edit instead of creating one. */
  sku?: any;
  /** The enterprise's items and recipes; given, a made product can be set up with its materials. */
  skus?: any[];
  recipes?: { product_id: string; material_id: string; kind: string; ratio: string }[];
  /** Opens the spec templates over this dialog. */
  onTemplates?: () => void;
  onClose: () => void;
  onCreated: (sku: CreatedSku) => void;
  /** Given, a new item can be saved with the dialog kept open for the next one. */
  onContinue?: (sku: CreatedSku) => void;
  /** Which prices the member is shown (by role). Left out when editing, a price the server left
   *  out of the item is hidden; when creating, both are shown. */
  prices?: { purchase: boolean; sale: boolean };
}

// The usual units to pick; any other can be typed.
const unitOptions = ['个', '吨', 'kg', 'g'];
// What the item is for, in the order a factory meets them; each decides the cards on the right.
const usageChoices: { usage: SkuUsage; label: string; hint: string; icon: React.ReactElement }[] = [
  { usage: 'SALE', label: '自产销售', hint: '自产或加工后卖给客户', icon: <FactoryOutlined /> },
  {
    usage: 'PURCHASE',
    label: '外购原料',
    hint: '只采购入库，如原料、包材',
    icon: <Inventory2Outlined />,
  },
  { usage: 'BOTH', label: '外购转售', hint: '外购后直接转售，如配件', icon: <SwapHoriz /> },
  {
    usage: 'INTERNAL',
    label: '自产自用',
    hint: '不采购也不销售，如半成品',
    icon: <ConstructionOutlined />,
  },
];

/**
 * Picks, for parameters still empty, the options the text already mentions: "500ml PET" picks
 * 500ml and PET. Longest option first, so "PET" is not read as "PE"; a number counts only on
 * its own, so 20 is not read from 200ml.
 */
function prefill(fields: SpecField[], values: Record<string, string>, text: string) {
  const upper = text.toUpperCase();
  const next = { ...values };
  for (const field of fields)
    if (!next[field.key]) {
      const hit = [...field.options]
        .sort((x, y) => y.length - x.length)
        .find((o) =>
          /^\d+$/.test(o)
            ? new RegExp(`(^|[^\\d.])${o}($|[^\\d.])`).test(upper)
            : upper.includes(o.toUpperCase()),
        );
      if (hit) next[field.key] = hit;
    }
  return next;
}

/**
 * Creates or edits an item: what it is on the left (name, spec by its category's template), and
 * on the right only what its usage needs: prices, what it is made of, what goes out with it.
 */
export default function SkuCreateDialog(p: Props) {
  const categories = p.categories ?? [];
  const editing = p.sku;
  const seesBuyPrice = p.prices?.purchase ?? (editing ? 'purchasePrice' in editing : true);
  const seesSellPrice = p.prices?.sale ?? (editing ? 'salesPrice' in editing : true);
  const [form, setForm] = useState({
    code: '',
    name: editing?.name ?? '',
    usage: (editing?.usage ?? p.defaultUsage) as SkuUsage,
    unit: editing?.unit ?? '个',
    unitWeight: editing?.unitWeight ? String(Number(editing.unitWeight)) : '',
    salesPrice: editing?.salesPrice != null ? plain(editing.salesPrice, PRICE_DECIMALS) : '',
    purchasePrice:
      editing?.purchasePrice != null ? plain(editing.purchasePrice, PRICE_DECIMALS) : '',
    // A new item finds its category from its name once typed (see nameTyped); the only one
    // there is needs no finding.
    categoryId: (editing?.categoryId ??
      (categories.length === 1 ? categories[0].id : '')) as string,
    attributes: (editing?.attributes ?? {}) as Record<string, string>,
  });
  const saved = (kind: string): RecipeLine[] =>
    (p.recipes ?? [])
      .filter((r) => editing && r.product_id === editing.id && r.kind === kind)
      .map((r) => ({ skuId: r.material_id, ratio: String(Number(r.ratio)) }));
  const [lines, setLines] = useState(() => saved('PRODUCE'));
  const [bundle, setBundle] = useState(() => saved('BUNDLE'));
  const [busy, setBusy] = useState(false);
  const [categoryMenu, setCategoryMenu] = useState<HTMLElement | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  // Once the name is in: a new item takes the category its name mentions, and empty
  // parameters take the options the name mentions.
  const nameTyped = () => {
    const categoryId =
      !editing && !form.categoryId
        ? (categories.find((c) => form.name.includes(c.name))?.id ?? '')
        : form.categoryId;
    const fields = categories.find((c) => c.id === categoryId)?.fields ?? [];
    setForm({ ...form, categoryId, attributes: prefill(fields, form.attributes, form.name) });
  };
  const [error, setError] = useState('');
  const usages = p.allowedUsages ?? usageChoices.map((c) => c.usage);
  const bought = form.usage === 'PURCHASE' || form.usage === 'BOTH';
  const selling = form.usage === 'SALE' || form.usage === 'BOTH';
  // Anything not only bought may be made here; its weight suggests the material a run uses.
  const made = form.usage !== 'PURCHASE';
  const category = categories.find((c) => c.id === form.categoryId);
  const fields = category?.fields ?? [];
  const materialField = fields.find((f) => f.key === 'material');
  // The template's weight parameter is the unit weight itself, typed in the spec.
  const weightField = fields.find((f) => f.key === 'weight');
  const values = weightField ? { ...form.attributes, weight: form.unitWeight } : form.attributes;
  const spec = specText(fields, values);
  // The template decides: its material parameter links the quote, and a category without one
  // (e.g. 包材) is never linked.
  const material = specMaterial(fields, form.attributes);
  const withRecipe = made && !!p.skus;
  const skuOf = (id: string) => p.skus?.find((s) => s.id === id);
  const parts = partsFor(lines, skuOf);
  // Bought materials first; a sale product can still be a part of another.
  const materials = (p.skus ?? [])
    .filter((s) => s.id !== editing?.id)
    .sort((x, y) => Number(x.usage === 'SALE') - Number(y.usage === 'SALE'));
  // An item is either used up making the product or sold with it, never both.
  const optionsFor = (other: RecipeLine[]) =>
    materials.filter((s) => !other.some((l) => l.skuId === s.id));

  async function save(keepOpen: boolean) {
    if (busy) return;
    const data = {
      code: form.code.trim(),
      name: form.name.trim(),
      usage: form.usage,
      unit: form.unit.trim(),
      categoryId: category?.id ?? null,
      attributes: form.attributes,
      // Packing varies by order, so the form no longer asks; an old value is kept as it was.
      packSize: editing?.packSize ?? null,
      unitWeight: made || weightField ? form.unitWeight.replace(/\.$/, '') : '',
      // Blank means "not priced yet"; it can still be set from the list later.
      // A price the member is not shown is left out, so it stays as it is.
      salesPrice: !seesSellPrice
        ? editing
          ? undefined
          : null
        : selling && form.salesPrice !== ''
          ? form.salesPrice.replace(/\.$/, '')
          : null,
      purchasePrice: !seesBuyPrice
        ? editing
          ? undefined
          : null
        : bought && form.purchasePrice !== ''
          ? form.purchasePrice.replace(/\.$/, '')
          : null,
      integerUnit: true,
      // Only bought items have no recipe; the quick create from an order leaves it out.
      recipe: !p.skus
        ? undefined
        : made
          ? lines
              .filter((l) => l.skuId)
              .map((l) => ({ skuId: l.skuId, ratio: String(parts.partsOf(l)) }))
          : [],
      bundle: !p.skus
        ? undefined
        : selling
          ? bundle.filter((l) => l.skuId).map((l) => ({ skuId: l.skuId, perPiece: l.ratio }))
          : [],
    };
    if (!data.name || !data.unit) {
      setError('请填写商品名称和库存单位');
      return;
    }
    if (!category) {
      setError('请选择规格模板');
      return;
    }
    const missing = fields.filter((f) => f.required && !values[f.key]?.trim());
    if (missing.length) {
      setError(`请填写${category.name}规格：${missing.map((f) => f.label).join('、')}`);
      return;
    }
    if (data.code && !/^[A-Za-z0-9_-]{1,50}$/.test(data.code)) {
      setError('商品编码只能包含字母、数字、- 和 _；也可以留空自动生成');
      return;
    }
    if (data.unitWeight && Number(data.unitWeight) <= 0) {
      setError('单重须大于零；不需要可留空');
      return;
    }
    if (made) {
      const missing = lines.find((l) => l.skuId && !(parts.partsOf(l) > 0));
      if (missing) {
        const sku = skuOf(missing.skuId);
        setError(`请填写 ${sku?.name} 的${byWeight(sku) ? '配比份数' : '每件用量'}`);
        return;
      }
      const unset = selling && bundle.find((l) => l.skuId && !(Number(l.ratio) > 0));
      if (unset) {
        setError(
          `请填写 ${skuOf(unset.skuId)?.name} 每${form.unit || '个'}配几${skuOf(unset.skuId)?.unit}`,
        );
        return;
      }
      if (parts.weighed.length && !data.unitWeight) {
        const names = parts.weighed.map((l) => skuOf(l.skuId)?.name).join('、');
        setError(`${names} 按重量扣减，请填写单重`);
        return;
      }
    }
    setBusy(true);
    setError('');
    try {
      const sku = editing
        ? await api<CreatedSku>(`/skus/${editing.id}`, data)
        : await api<CreatedSku>('/skus', data);
      if (keepOpen && p.onContinue) {
        // The next item is usually of the same kind: usage, unit and template stay.
        p.onContinue(sku);
        setForm({
          ...form,
          code: '',
          name: '',
          attributes: {},
          unitWeight: '',
          salesPrice: '',
          purchasePrice: '',
        });
        setLines([]);
        setBundle([]);
        nameRef.current?.focus();
      } else p.onCreated(sku);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const bundleFigure = (line: RecipeLine, sku: any, set: (ratio: string) => void) => (
    <TextField
      size="small"
      fullWidth
      value={line.ratio}
      disabled={busy}
      placeholder="如 1"
      slotProps={{
        htmlInput: { inputMode: 'decimal', 'aria-label': `${sku.name} 配套数量` },
        input: {
          startAdornment: <InputAdornment position="start">每{form.unit || '个'}</InputAdornment>,
          endAdornment: <InputAdornment position="end">{sku.unit}</InputAdornment>,
        },
      }}
      onChange={(e) => set(decimalInput(e.target.value, 4))}
    />
  );
  const pickCategory = (categoryId: string) => {
    const next = categories.find((c) => c.id === categoryId);
    setForm({
      ...form,
      categoryId,
      attributes: prefill(next?.fields ?? [], form.attributes, form.name),
    });
  };
  const choices = usageChoices.filter((c) => usages.includes(c.usage));
  const priceField = (label: string, key: 'salesPrice' | 'purchasePrice', helper?: string) => (
    <Box>
      <FieldLabel hint="选填">{label}</FieldLabel>
      <TextField
        fullWidth
        size="small"
        placeholder="0.00"
        value={form[key]}
        disabled={busy}
        helperText={helper}
        slotProps={{
          htmlInput: { inputMode: 'decimal', 'aria-label': label },
          input: {
            endAdornment: (
              <InputAdornment position="end">元 / {form.unit || '单位'}</InputAdornment>
            ),
          },
          formHelperText: { sx: { mx: 0 } },
        }}
        onChange={(e) => setForm({ ...form, [key]: decimalInput(e.target.value, PRICE_DECIMALS) })}
      />
    </Box>
  );
  // A sold item's price card: its quote, and whether it follows a material's market.
  const pricing = selling;
  const linkNote =
    materialField && !material
      ? form.attributes.material
        ? `该${materialField.label}无原料行情，报价不联动`
        : `选好${materialField.label}后，报价随该原料行情联动`
      : '';
  const weight = Number(form.unitWeight) || 0;
  const smallText = { size: 'small' as const, sx: { fontSize: 12.5, minWidth: 0, px: 1 } };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      onClose={() => !busy && p.onClose()}
      // Pinned to the top and of a fixed height, each column scrolling on its own, so picking a
      // template or adding rows never resizes it.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      slotProps={{
        paper: {
          sx: {
            mt: 6,
            maxWidth: 1100,
            height: p.skus ? 'min(820px, calc(100% - 96px))' : undefined,
          },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          save(false);
        }}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '& svg': { fontSize: 20 },
            }}
          >
            {editing ? <EditOutlined /> : <AddBoxOutlined />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4 }}>
                {editing ? '编辑商品' : (p.title ?? '新增物料或产品')}
              </Typography>
              {editing && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={editing.code}
                  sx={{ height: 22, fontSize: 12, fontWeight: 600 }}
                />
              )}
            </Box>
            {/* What the chosen usage means, so the switch beside it explains itself. */}
            <Typography color="text.secondary" sx={{ fontSize: 12.5 }} noWrap>
              {editing ? `库存单位 ${editing.unit} · ` : ''}
              {usageChoices.find((c) => c.usage === form.usage)?.hint}
            </Typography>
          </Box>
          {/* What the item is for decides the cards on the right; one choice is no choice. */}
          {choices.length > 1 && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.usage}
              disabled={busy}
              aria-label="业务用途"
              onChange={(_, usage: SkuUsage | null) => usage && setForm({ ...form, usage })}
              // A segmented control: options sit borderless in a light track and the chosen one
              // is filled, so the current usage reads at a glance.
              sx={{
                ml: 'auto',
                p: 0.5,
                gap: 0.5,
                bgcolor: SURFACE,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '& .MuiToggleButton-root.MuiToggleButtonGroup-grouped': {
                  m: 0,
                  border: 0,
                  borderRadius: 1,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                },
              }}
            >
              {choices.map((c) => (
                <Tooltip key={c.usage} title={c.hint} describeChild>
                  <ToggleButton
                    value={c.usage}
                    sx={{
                      gap: 0.75,
                      px: 1.5,
                      py: 0.5,
                      fontSize: 13,
                      fontWeight: 600,
                      '& svg': { fontSize: 17 },
                    }}
                  >
                    {c.icon}
                    {c.label}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          )}
          <IconButton
            aria-label="关闭"
            disabled={busy}
            onClick={p.onClose}
            sx={{ ml: choices.length > 1 ? 0 : 'auto', mr: -1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: 2,
            px: 3,
            py: 2.5,
            bgcolor: SURFACE,
            overflow: { xs: 'auto', md: 'hidden' },
            // Each column scrolls on its own.
            '& > *': { overflow: { md: 'auto' }, minHeight: 0 },
          }}
        >
          {/* What the item is. */}
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Section icon={<TuneOutlined />} title="基础信息">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: editing ? '1fr' : 'minmax(0, 1fr) 140px 104px',
                  },
                  gap: 1.5,
                }}
              >
                <Box>
                  <FieldLabel required>商品名称</FieldLabel>
                  <TextField
                    autoFocus={!editing}
                    required
                    fullWidth
                    size="small"
                    placeholder="如 24/400 白色 PE 瓶盖"
                    value={form.name}
                    disabled={busy}
                    inputRef={nameRef}
                    slotProps={{ htmlInput: { maxLength: 100, 'aria-label': '商品名称' } }}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    onBlur={nameTyped}
                  />
                </Box>
                {!editing && (
                  <Box>
                    <FieldLabel hint="选填">编码</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={category ? `${category.codePrefix}-自动编号` : '自动生成'}
                      value={form.code}
                      disabled={busy}
                      slotProps={{
                        htmlInput: {
                          maxLength: 50,
                          'aria-label': '商品编码',
                          style: { textTransform: 'uppercase' },
                        },
                      }}
                      onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    />
                  </Box>
                )}
                {!editing && (
                  <Box>
                    <FieldLabel required>库存单位</FieldLabel>
                    <Tooltip title="建档后不能修改" placement="top">
                      <Autocomplete
                        freeSolo
                        disableClearable
                        size="small"
                        options={unitOptions}
                        value={form.unit}
                        disabled={busy}
                        // Any text counts as typed: a unit not in the list is kept as written.
                        onInputChange={(_, unit) => setForm((f) => ({ ...f, unit }))}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            slotProps={{
                              ...params.slotProps,
                              htmlInput: {
                                ...params.slotProps.htmlInput,
                                maxLength: 12,
                                'aria-label': '库存单位',
                              },
                            }}
                          />
                        )}
                      />
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Section>

            <Section
              icon={<GridViewOutlined />}
              title="规格参数"
              extra={
                category && (
                  <Chip
                    size="small"
                    label={category.name}
                    sx={{ height: 22, fontSize: 12, fontWeight: 500 }}
                  />
                )
              }
              action={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {category && categories.length > 1 && (
                    <Button
                      {...smallText}
                      color="inherit"
                      startIcon={<SwapHoriz />}
                      disabled={busy}
                      onClick={(e) => setCategoryMenu(e.currentTarget)}
                    >
                      换模板
                    </Button>
                  )}
                  {p.onTemplates && categories.length > 0 && (
                    <Button
                      {...smallText}
                      startIcon={<TuneOutlined />}
                      disabled={busy}
                      onClick={p.onTemplates}
                    >
                      模板参数
                    </Button>
                  )}
                </Box>
              }
            >
              <Menu
                anchorEl={categoryMenu}
                open={!!categoryMenu}
                onClose={() => setCategoryMenu(null)}
              >
                {categories.map((c) => (
                  <MenuItem
                    key={c.id}
                    selected={c.id === form.categoryId}
                    onClick={() => {
                      pickCategory(c.id);
                      setCategoryMenu(null);
                    }}
                  >
                    {c.name}
                  </MenuItem>
                ))}
              </Menu>
              {category ? (
                <Stack spacing={2}>
                  <SpecPanel
                    fields={fields}
                    values={values}
                    disabled={busy}
                    onChange={({ weight, ...attributes }) =>
                      setForm({
                        ...form,
                        attributes,
                        unitWeight: weightField ? (weight ?? '') : form.unitWeight,
                      })
                    }
                  />
                  <Note icon={<DescriptionOutlined />}>
                    规格摘要：
                    <Box
                      component="span"
                      sx={{ color: spec ? 'text.primary' : 'text.disabled', fontWeight: 600 }}
                    >
                      {spec || '选择参数后自动生成'}
                    </Box>
                  </Note>
                </Stack>
              ) : categories.length ? (
                // No template picked yet: they are the choice to make here.
                <Box>
                  <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1.25 }}>
                    选择规格模板，按模板填写规格
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {categories.map((c) => (
                      <Chip
                        key={c.id}
                        label={c.name}
                        clickable
                        variant="outlined"
                        disabled={busy}
                        onClick={() => pickCategory(c.id)}
                        sx={{ height: 28, fontSize: 13, minWidth: 56 }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                // Every item is described by a template, so there is none to file without one.
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    还没有规格模板，建档前先添加
                  </Typography>
                  {p.onTemplates && (
                    <Button size="small" variant="outlined" onClick={p.onTemplates}>
                      添加规格模板
                    </Button>
                  )}
                </Box>
              )}
            </Section>
          </Stack>

          {/* How it is bought, sold and made; only what the usage needs. */}
          <Stack spacing={2} sx={ON_SURFACE}>
            {bought && seesBuyPrice && (
              <Section icon={<ShoppingCartOutlined />} title="采购价">
                {priceField(
                  '参考采购价',
                  'purchasePrice',
                  '新建采购单时带出；有上次成交价时优先用成交价',
                )}
              </Section>
            )}
            {pricing && seesSellPrice && (
              <Section
                icon={<PaymentsOutlined />}
                title="销售定价"
                action={material && <Tag tone="success">随 {material} 行情联动</Tag>}
              >
                <Stack spacing={1}>
                  {priceField(
                    '销售报价',
                    'salesPrice',
                    // A changed quote is the new starting point of the linkage.
                    editing && material ? `改价后按今天的 ${material} 行情重新起算联动` : undefined,
                  )}
                  {linkNote && (
                    <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                      {linkNote}
                    </Typography>
                  )}
                  {category && !materialField && (
                    <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                      该模板没有材质参数，报价不随原料行情联动
                    </Typography>
                  )}
                </Stack>
              </Section>
            )}
            {made && (
              <Section icon={<ScienceOutlined />} title="生产配料">
                <Stack spacing={1.5}>
                  {weightField ? (
                    // Typed once, in the spec; shown here as what the recipe works from.
                    <Note icon={<ScienceOutlined />}>
                      单重
                      <Box
                        component="span"
                        sx={{ color: 'text.primary', fontWeight: 600, mx: 0.5 }}
                      >
                        {weight ? `${weight} g / ${form.unit || '个'}` : '未填写'}
                      </Box>
                      取自规格中的{weightField.label}
                    </Note>
                  ) : (
                    <Box>
                      <FieldLabel hint="选填">单重</FieldLabel>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="如 25"
                        value={form.unitWeight}
                        disabled={busy}
                        helperText="生产入库时按 单重 × 数量 计算原料用量"
                        slotProps={{
                          htmlInput: { inputMode: 'decimal', 'aria-label': '单重' },
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                g / {form.unit || '个'}
                              </InputAdornment>
                            ),
                          },
                          formHelperText: { sx: { mx: 0 } },
                        }}
                        onChange={(e) =>
                          setForm({ ...form, unitWeight: decimalInput(e.target.value, 4) })
                        }
                      />
                    </Box>
                  )}
                  {withRecipe && (
                    <>
                      <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                        按重量计的原料填份数，自动折算占比和单耗
                      </Typography>
                      <RecipeTable
                        lines={lines}
                        options={optionsFor(bundle)}
                        unit={form.unit}
                        unitWeight={weight}
                        disabled={busy}
                        max={20}
                        onChange={setLines}
                      />
                    </>
                  )}
                </Stack>
              </Section>
            )}
            {selling && p.skus && (
              <Section icon={<LocalShippingOutlined />} title="出库配套">
                <Typography color="text.secondary" sx={{ fontSize: 12.5, mb: 1.25 }}>
                  随产品发货、价格已含在报价里，如瓶盖
                </Typography>
                <MaterialRows
                  lines={bundle}
                  options={optionsFor(lines)}
                  max={10}
                  disabled={busy}
                  placeholder="选择配件"
                  addLabel="添加配件"
                  onChange={setBundle}
                  figure={bundleFigure}
                  figureWidth={150}
                />
              </Section>
            )}
          </Stack>
        </Box>
        <DialogActions
          sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}
        >
          <Typography color="text.secondary" sx={{ fontSize: 12.5, mr: 'auto' }}>
            {editing ? '已下的订单保留下单时的名称和规格' : '保存档案不会增加库存'}
          </Typography>
          <Button color="inherit" disabled={busy} onClick={p.onClose}>
            取消
          </Button>
          {!editing && p.onContinue && (
            <Button
              variant="outlined"
              disabled={busy || !categories.length}
              onClick={() => save(true)}
            >
              保存并继续新增
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={busy || !categories.length}>
            {busy ? '正在保存…' : editing ? '保存修改' : '保存档案'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
